import { AsyncPipe, CommonModule, NgIf, NgSwitch } from '@angular/common';
import { Component, inject, OnInit, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ApplicationProfilePortalTheme } from '@app/@shared/models';
import {
  EnrollmentAnalyticsRequest,
  EnrollmentResponse,
  EnrollmentSupportEmailRequest,
  UpdateEnrollmentRequest,
} from '@app/resources/services';
import { RegistrationService } from '@app/resources/services/registration.service';
import {
  LoadingSpinnerComponent,
  SimpleTextControlComponent,
  InternationalPhoneControlComponent,
  PercentageControlComponent,
  PriceControlComponent,
  PositiveIntegerControlComponent,
} from '@ep/shared';
import { Button } from 'primeng/button';
import { AccordionModule } from 'primeng/accordion';
import { DialogModule } from 'primeng/dialog';
import { TimezonePipe } from '../areas/admin/submodules/locations/pipes/timezone.pipe';
import { TransactionLoadingService } from '@app/areas/transactions/services/transaction-loading.service';
import { EnrollmentAPIService } from '@app/enroll/services';
import { tap } from 'rxjs';
import { MessageService } from 'primeng/api';
import { MerchantRegistrationType } from '@app/@shared/enums';
import { UsTimezoneDropdownComponent } from '@app/@shared/form-controls/us-timezone-dropdown/us-timezone-dropdown.component';
import { EnrollmentStatusTypes } from '@app/@shared/enums/enrollment-status-types.enum';
import { AuthModule } from '@app/auth/auth.module';
import { TpnAnalyticsTypes } from '@app/@shared/enums/tpn-analytics-types.enum';

@Component({
  selector: 'app-registration',
  templateUrl: './merchant-registration.component.html',
  styleUrls: ['./merchant-registration.component.scss'],
  standalone: true,
  imports: [
    SimpleTextControlComponent,
    Button,
    NgIf,
    ReactiveFormsModule,
    AsyncPipe,
    TimezonePipe,
    LoadingSpinnerComponent,
    InternationalPhoneControlComponent,
    NgSwitch,
    CommonModule,
    PercentageControlComponent,
    PriceControlComponent,
    PositiveIntegerControlComponent,
    UsTimezoneDropdownComponent,
    DialogModule,
    AuthModule,
    AccordionModule,
  ],
})
export class MerchantRegistrationComponent implements OnInit {
  private readonly registrationService = inject(RegistrationService);
  private readonly loadingService = inject(TransactionLoadingService);
  private readonly enrollmentApiService = inject(EnrollmentAPIService);
  private readonly messagingService = inject(MessageService);
  private readonly fb = inject(FormBuilder);
  private readonly route = inject(ActivatedRoute);
  protected readonly EnrollmentStatusTypes = EnrollmentStatusTypes;
  protected readonly MerchantRegistrationTypeEnum = MerchantRegistrationType;

  protected tpnForm!: FormGroup;
  protected merchantForm!: FormGroup;
  protected cashBackForm!: FormGroup;
  protected pointsForm!: FormGroup;
  protected bonusPointsForm!: FormGroup;
  protected instantOfferForm!: FormGroup;
  protected bonusOfferForm!: FormGroup;

  protected portalTheme: ApplicationProfilePortalTheme | null = null;
  protected coalitionName: string = '';
  protected coalitionId: string = ''; // Default value, will be overridden if present in URL
  protected validGuid: boolean = false;

  protected enrollmentResponse: EnrollmentResponse | null = null;
  protected isMerchantFormEditing: boolean = false;
  protected showPendingBanner: boolean = false;
  protected showTpnTutorialModal: boolean = false;
  protected showHelpModal: boolean = false;
  protected helpForm!: FormGroup;
  protected accordionActiveIndex: number[] = [];
  protected isExpanded: boolean = false;
  protected hasSignificantChanges: boolean = false;
  protected merchantRegistrationType: MerchantRegistrationType | undefined;
  protected currentStepIndex: number = 0;
  protected helpNeeded: boolean = false;

  private originalMerchantFormValues: any = null;
  private acceptedTC: boolean = false;
  private saveEnrollmentOrigin: boolean = false;
  protected isAdmin: boolean = false;

  protected enrollmentStatus: EnrollmentStatusTypes = EnrollmentStatusTypes.NotStarted;
  protected enrollmentId: string = '';
  protected sessionId: string = '';
  protected campaignId: string = '';

  public isLoading$ = this.loadingService.isLoading$;

  ngOnInit(): void {
    this.loadingService.setLoadingStatus(true);

    this.route.queryParams.subscribe((params) => {
      if (params['Cid']) {
        this.coalitionId = params['Cid'];
        this.merchantRegistrationType = MerchantRegistrationType.Sdk_Pline;
        this.getPortalTheme();
        this.isInstantOfferSelected = true;
      }

      if (params['id']) {
        this.enrollmentId = params['id'];
        this.getTpnDataByEnrollmentId(this.enrollmentId);
        this.getPortalTheme(this.enrollmentId);
        this.merchantRegistrationType = MerchantRegistrationType.Terminal_Qd;
        this.isInstantOfferSelected = true;
      }

      if (params['EP']) {
        this.isAdmin = true;
      }

      if (params['enroll']) {
        this.merchantRegistrationType = MerchantRegistrationType.Coalition_Flow;
        this.enrollmentId = params['enroll'];
        this.getTpnDataByEnrollmentId(this.enrollmentId);
        this.getPortalTheme(this.enrollmentId);
      }

      if (params['c']) {
        this.campaignId = params['c'];
      }

      setTimeout(() => {
        const existingSessionId = sessionStorage.getItem('sessionId');
        if (existingSessionId) {
          this.sessionId = existingSessionId;
        } else {
          this.sessionId = this.generateSessionId();
          sessionStorage.setItem('sessionId', this.sessionId);
        }
        this.createEnrollmentAnalytics();
      }, 1_000);
    });

    this.initializeTpnForm();
    this.initializeMerchantForm();

    this.initializeCashBackForm();
    this.initializePointsForm();

    this.initializeInstantOfferForm();
    this.initializeBonusOfferForm();
    this.initializeBonusPointsForm();
    this.initializeHelpForm();
  }

  private isMobileDevice(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();
    return /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
  }

  // Forms Initialization
  // --------------------------------------------------
  protected initializeTpnForm(): void {
    this.tpnForm = this.fb.group({
      tpn: ['', Validators.required],
    });
  }

  protected initializeMerchantForm(): void {
    this.merchantForm = this.fb.group({
      FirstName: ['', Validators.required],
      LastName: ['', Validators.required],
      Email: ['', [Validators.required, this.emailValidator]],
      BusinessName: ['', Validators.required],
      BusinessPhone: [
        '',
        [Validators.required, Validators.pattern(/^(\+?1[-.\s]?)?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/)],
      ],
      Address: ['', Validators.required],
      Timezone: ['', Validators.required],
    });

    // Reset original values when form is initialized
    this.originalMerchantFormValues = null;
    this.hasSignificantChanges = false;
  }

  protected initializeCashBackForm(): void {
    this.cashBackForm = this.fb.group({
      Name: ['', Validators.required],
      cashBackPercentage: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
    });
  }

  protected initializePointsForm(): void {
    this.pointsForm = this.fb.group({
      Name: ['', Validators.required],
      QualifierAmount: [1, [Validators.required, Validators.min(0)]],
      EarnPoints: [1, [Validators.required, Validators.min(1)]],
      RewardGoal: [100, [Validators.required, Validators.min(1)]],
      RewardAmount: [10, [Validators.required, Validators.min(0)]],
    });
  }

  protected initializeBonusPointsForm(): void {
    this.bonusPointsForm = this.fb.group({
      BonusPoints: [0, [Validators.required, Validators.min(0)]],
    });
  }

  protected initializeInstantOfferForm(): void {
    this.instantOfferForm = this.fb.group({
      InstantReward: [10, [Validators.required, Validators.min(0), Validators.max(100)]],
      MaxRewardAmount: [100, [Validators.required, Validators.min(0)]],
    });
  }

  protected initializeBonusOfferForm(): void {
    this.bonusOfferForm = this.fb.group({
      BonusReward: [0, [Validators.required, Validators.min(0), Validators.max(100)]],
      MaxRewardAmount: [0, [Validators.required, Validators.min(0)]],
    });
  }

  protected initializeHelpForm(): void {
    this.helpForm = this.fb.group({
      FirstName: ['', Validators.required],
      BusinessPhone: [
        '',
        [Validators.required, Validators.pattern(/^(\+?1[-.\s]?)?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}$/)],
      ],
    });
  }

  // Theme
  // --------------------------------------------------
  protected getPortalTheme(guid?: string): void {
    const themeObservable = guid
      ? this.registrationService.getCoalitionThemeByEnrollmentId(guid)
      : this.registrationService.getCoalitionTheme(this.coalitionId);

    themeObservable.subscribe((theme: ApplicationProfilePortalTheme | null) => {
      if (theme) {
        this.portalTheme = theme;
        this.coalitionName = theme.CoalitionName || '';
        this.setCSSCustomProperties();
        this.loadingService.setLoadingStatus(false);
      }
    });
  }

  private setCSSCustomProperties(): void {
    if (this.portalTheme?.Theme) {
      const theme = this.portalTheme.Theme;
      // Set CSS custom properties
      document.documentElement.style.setProperty('--sidebar-logo-bg', theme.sidebar?.logoBackgroundColor);
      document.documentElement.style.setProperty('--header-bg-primary', theme.header?.backgroundColor);
      document.documentElement.style.setProperty('--header-primary', theme.header?.primary);
      document.documentElement.style.setProperty('--main-content-bg', theme.backgroundColor);
      document.documentElement.style.setProperty('--header-items-color', theme.header?.itemsColor);
      document.documentElement.style.setProperty('--header-text-color', theme.header?.textColor);

      document.documentElement.style.setProperty('--sidebar-bg-secondary', theme.sidebar?.backgroundColor?.secondary);
      document.documentElement.style.setProperty('--sidebar-bg-active', theme.sidebar?.backgroundColor?.active);
    }
  }

  // Fetch/Post Data
  // --------------------------------------------------
  protected getTpnData(): void {
    this.showPendingBanner = false;
    const tpnForm = this.tpnForm.value;
    if (this.tpnForm.valid) {
      if (tpnForm.tpn) {
        this.registrationService
          .getTpnData(this.coalitionId, tpnForm.tpn, this.sessionId, this.isAdmin.toString())
          .subscribe((response: EnrollmentResponse) => {
            this.enrollmentId = response.EnrollmentId;
            this.enrollmentStatus = response.StatusType!;
            this.handleEnrollmentResponse(response);
          });
      }
    }
  }

  protected getTpnDataByEnrollmentId(enrollmentId: string): void {
    this.registrationService.getTpnDataByEnrollmentId(enrollmentId).subscribe((response: EnrollmentResponse) => {
      this.handleEnrollmentResponse(response);
      this.coalitionId = response.Data?.CoalitionId.toString()!;
      this.validGuid = this.enrollmentResponse?.EnrollmentId !== undefined;
      this.enrollmentStatus = response.StatusType!;
    });
  }

  private handleEnrollmentResponse(response: EnrollmentResponse): void {
    if (response.EnrollmentId === undefined) {
      this.merchantForm.reset();
      this.isMerchantFormEditing = true;
      this.goToStep(1);
      return;
    }

    this.sessionId = this.enrollmentId;
    sessionStorage.setItem('sessionId', this.sessionId);

    if (response.StatusType === EnrollmentStatusTypes.Complete) {
      this.goToStep(4);
      return;
    }

    if (response.StatusType === EnrollmentStatusTypes.Pending) {
      this.showPendingBanner = true;
    }

    this.enrollmentResponse = response;
    this.populateMerchantForm(this.enrollmentResponse);
    this.populateBonusOfferForm(this.enrollmentResponse);
    this.populateInstantOfferForm(this.enrollmentResponse);
    this.populateCashBackForm(this.enrollmentResponse);
    this.populatePointsForm(this.enrollmentResponse);
    this.populateBonusPointsForm(this.enrollmentResponse);

    if (
      !this.isInstantOfferSelected &&
      !this.isBonusOfferSelected &&
      !this.isBonusPointsSelected &&
      this.merchantRegistrationType !== MerchantRegistrationType.Coalition_Flow
    ) {
      this.isInstantOfferSelected = true;
    }

    this.isMerchantFormEditing = !this.validateEnrollmentResponse(this.enrollmentResponse);
    this.goToStep(1);
  }

  protected updateExistingEnrollmentToDefault(): void {
    const merchantFormValue = this.merchantForm.value;

    var updateEnrollmentRequest: UpdateEnrollmentRequest = {
      EnrollmentId: this.enrollmentResponse?.EnrollmentId!,
      MerchantAccount: {
        ContactInformation: {
          PhoneNumber: merchantFormValue.BusinessPhone,
          FirstName: merchantFormValue.FirstName || '',
          LastName: merchantFormValue.LastName || '',
          EmailAddress: merchantFormValue.Email || '',
          PosIdent: '123456',
        },
        BusinessInformation: {
          Name: merchantFormValue.BusinessName || '',
          Address: merchantFormValue.Address || '',
          PhoneNumber: merchantFormValue.BusinessPhone || '',
          TimeZone: merchantFormValue.Timezone || '',
        },
      },
      LoyaltyProgram: {
        CashBack: null,
        Points: {
          Name: `${merchantFormValue.BusinessName} Rewards`,
          QualifierAmount: 1,
          PointsEarned: 1,
          RewardQualifier: 100,
          RewardAmount: 10.0,
        },
      },
      EnrollmentOffer: {
        InstantOffer: {
          RewardPercentage: 0,
          MaxRewardAmount: 100.0,
        },
        BonusOffer: null,
        EnrollmentPoints: null,
      },
      HasSignificantChanges:
        this.hasSignificantChanges && this.merchantRegistrationType !== MerchantRegistrationType.Coalition_Flow
          ? true
          : null,
      SendNotificationEmail: this.shouldSendNotificationEmail(),
      CreateDevice: this.shouldCreateDevice(),
      MerchantRegistrationType: this.merchantRegistrationType,
      AcceptedTermsAndConditions:
        this.enrollmentStatus === EnrollmentStatusTypes.SelfService && this.acceptedTC ? true : null,
      AnalyticsEventType: TpnAnalyticsTypes.TpnComplete,
    };

    this.enrollmentApiService
      .updateEnrollment(updateEnrollmentRequest)
      .pipe(
        tap((response: any) => {
          this.enrollmentStatus = response;

          if (response?.error) {
            this.messagingService.add({
              severity: 'error',
              summary: 'Error Updating Enrollment',
              detail: 'An error occurred while trying to save enrollment information',
            });
          } else {
            this.merchantForm.markAsPristine();
            this.showPendingBanner = false;
            this.goToStep(4);
          }
        })
      )
      .subscribe();
  }

  protected saveEnrollment(): void {
    // If Sdk_Pline registration type and the TPN was found, show Terms and Conditions first.
    if (
      this.merchantRegistrationType === MerchantRegistrationType.Sdk_Pline &&
      !this.isAdmin &&
      this.enrollmentStatus === EnrollmentStatusTypes.SelfService
    ) {
      this.saveEnrollmentOrigin = true;
      this.goToStep(5);
      this.updateEnrollmentData();
      return;
    }

    this.saveCustomEnrollment();
  }

  private saveCustomEnrollment(): void {
    // Merchant Form
    const merchantFormValue = this.merchantForm.value;

    // TPN Form
    const tpnForm = this.tpnForm.value;

    // Custom Enrollment Offer Form
    const instantOfferFormValue = this.instantOfferForm.value;
    const bonusOfferFormValue = this.bonusOfferForm.value;

    // Custom Loyalty Program Form
    const cashBackFormValue = this.cashBackForm.value;
    const pointsFormValue = this.pointsForm.value;

    var defaultAdditionalInformation: UpdateEnrollmentRequest = {
      EnrollmentId: this.enrollmentId || '',
      MerchantAccount: {
        ContactInformation: {
          FirstName: merchantFormValue.FirstName || '',
          LastName: merchantFormValue.LastName || '',
          PhoneNumber: merchantFormValue.BusinessPhone || '',
          EmailAddress: merchantFormValue.Email || '',
          PosIdent: '123456',
        },
        BusinessInformation: {
          Name: merchantFormValue.BusinessName || '',
          PhoneNumber: merchantFormValue.BusinessPhone || '',
          Address: merchantFormValue.Address || '',
          TimeZone: merchantFormValue.Timezone || '',
        },
      },
      LoyaltyProgram: {
        CashBack: this.isCustomCashBackSelected
          ? {
              Name: cashBackFormValue.Name,
              Percentage: cashBackFormValue.cashBackPercentage,
            }
          : null,
        Points:
          this.isCustomPointsSelected || this.isBonusPointsSelected
            ? {
                Name: pointsFormValue.Name || `${merchantFormValue.BusinessName} Rewards`,
                QualifierAmount: pointsFormValue.QualifierAmount,
                PointsEarned: pointsFormValue.EarnPoints,
                RewardQualifier: pointsFormValue.RewardGoal,
                RewardAmount: pointsFormValue.RewardAmount,
              }
            : null,
      },
      EnrollmentOffer: {
        InstantOffer: this.isInstantOfferSelected
          ? {
              RewardPercentage: instantOfferFormValue.InstantReward,
              MaxRewardAmount: instantOfferFormValue.MaxRewardAmount,
            }
          : null,
        BonusOffer: this.isBonusOfferSelected
          ? {
              RewardPercentage: bonusOfferFormValue.BonusReward,
              MaxRewardAmount: bonusOfferFormValue.MaxRewardAmount,
            }
          : null,
        EnrollmentPoints: this.isBonusPointsSelected
          ? {
              Points: this.bonusPointsForm.value.BonusPoints,
            }
          : null,
      },
      HasSignificantChanges:
        this.hasSignificantChanges && this.merchantRegistrationType !== MerchantRegistrationType.Coalition_Flow
          ? true
          : null,
      SendNotificationEmail: this.shouldSendNotificationEmail(),
      CreateDevice: this.shouldCreateDevice(),
      MerchantRegistrationType: this.merchantRegistrationType,
      AcceptedTermsAndConditions:
        this.enrollmentStatus === EnrollmentStatusTypes.SelfService && this.acceptedTC ? true : null,
      AnalyticsEventType: TpnAnalyticsTypes.TpnComplete,
    };

    this.enrollmentApiService
      .updateEnrollment(defaultAdditionalInformation)
      .pipe(
        tap((response: any) => {
          this.enrollmentStatus = response;

          if (response?.error) {
            this.messagingService.add({
              severity: 'error',
              summary: 'Error Updating Enrollment',
              detail: 'An error occurred while trying to save enrollment information',
            });
          } else {
            this.merchantForm.markAsPristine();
            this.showPendingBanner = this.hasSignificantChanges;
            this.goToStep(4);
          }
        })
      )
      .subscribe();
  }

  protected submitHelpRequest(): void {
    if (this.helpForm.valid) {
      var enrollmentSupportEmailRequest: EnrollmentSupportEmailRequest = {
        EnrollmentId: this.enrollmentResponse?.EnrollmentId || '',
        FirstName: this.helpForm.value.FirstName,
        BusinessPhone: this.helpForm.value.BusinessPhone,
        BusinessName: this.merchantForm.value.BusinessName || null,
        LastName: this.merchantForm.value.LastName || null,
        EmailAddress: this.merchantForm.value.Email || null,
        Address: this.merchantForm.value.Address || null,
      };

      this.helpNeeded = true;

      this.enrollmentApiService.sendSupportEmail(enrollmentSupportEmailRequest).subscribe((response: any) => {
        this.closeHelpModal();
        this.goToStep(4);
      });

      var createEnrollmentAnalyticsRequest: EnrollmentAnalyticsRequest = {
        EnrollmentId: this.sessionId,
        EventType: TpnAnalyticsTypes.TpnCompleteHelp,
      };

      this.enrollmentApiService.createEnrollmentAnalytics(createEnrollmentAnalyticsRequest).subscribe();
    }
  }

  // Update the Enrollment Data only
  protected updateEnrollmentData(): void {
    // Merchant Form
    const merchantFormValue = this.merchantForm.value;

    // Custom Enrollment Offer Form
    const instantOfferFormValue = this.instantOfferForm.value;
    const bonusOfferFormValue = this.bonusOfferForm.value;

    // Custom Loyalty Program Form
    const cashBackFormValue = this.cashBackForm.value;
    const pointsFormValue = this.pointsForm.value;

    var updateEnrollmentRequest: UpdateEnrollmentRequest = {
      UpdateDateTime: new Date(),
      EnrollmentId: this.enrollmentId,
      MerchantAccount: {
        ContactInformation: {
          FirstName: merchantFormValue.FirstName || '',
          LastName: merchantFormValue.LastName || '',
          PhoneNumber: merchantFormValue.BusinessPhone || '',
          EmailAddress: merchantFormValue.Email || '',
          PosIdent: '123456',
        },
        BusinessInformation: {
          Name: merchantFormValue.BusinessName || '',
          PhoneNumber: merchantFormValue.BusinessPhone || '',
          Address: merchantFormValue.Address || '',
          TimeZone: merchantFormValue.Timezone || '',
        },
      },
      LoyaltyProgram: {
        CashBack: this.isCustomCashBackSelected
          ? {
              Name: cashBackFormValue.Name,
              Percentage: cashBackFormValue.cashBackPercentage,
            }
          : null,
        Points:
          this.isCustomPointsSelected || this.isBonusPointsSelected
            ? {
                Name: pointsFormValue.Name || `${merchantFormValue.BusinessName} Rewards`,
                QualifierAmount: pointsFormValue.QualifierAmount,
                PointsEarned: pointsFormValue.EarnPoints,
                RewardQualifier: pointsFormValue.RewardGoal,
                RewardAmount: pointsFormValue.RewardAmount,
              }
            : null,
      },
      EnrollmentOffer: {
        InstantOffer: this.isInstantOfferSelected
          ? {
              RewardPercentage: instantOfferFormValue.InstantReward,
              MaxRewardAmount: instantOfferFormValue.MaxRewardAmount,
            }
          : null,
        BonusOffer: this.isBonusOfferSelected
          ? {
              RewardPercentage: bonusOfferFormValue.BonusReward,
              MaxRewardAmount: bonusOfferFormValue.MaxRewardAmount,
            }
          : null,
        EnrollmentPoints: this.isBonusPointsSelected
          ? {
              Points: this.bonusPointsForm.value.BonusPoints,
            }
          : null,
      },
      AnalyticsEventType: this.getUpdateEnrollmentAnalyticsType(),
    };

    this.enrollmentApiService.updateEnrollmentData(updateEnrollmentRequest).pipe().subscribe();
  }

  protected createEnrollmentAnalytics(): void {
    var createEnrollmentAnalyticsRequest: EnrollmentAnalyticsRequest = {
      EnrollmentId: this.sessionId,
      EventType: this.getMerchantRegistrationAnalyticsType(),
      CampaignId: this.campaignId || undefined,
    };

    this.enrollmentApiService.createEnrollmentAnalytics(createEnrollmentAnalyticsRequest).subscribe();
  }

  protected createCallSupportAnalytics(): void {
    var createEnrollmentAnalyticsRequest: EnrollmentAnalyticsRequest = {
      EnrollmentId: this.sessionId,
      EventType: TpnAnalyticsTypes.TpnCallSupport,
    };

    this.enrollmentApiService.createEnrollmentAnalytics(createEnrollmentAnalyticsRequest).subscribe();
  }

  protected createNeedHelpAnalytics(): void {
    var createEnrollmentAnalyticsRequest: EnrollmentAnalyticsRequest = {
      EnrollmentId: this.sessionId,
      EventType: TpnAnalyticsTypes.TpnHelpClicked,
    };

    this.enrollmentApiService.createEnrollmentAnalytics(createEnrollmentAnalyticsRequest).subscribe();
  }

  // Forms populate
  // --------------------------------------------------
  protected populateMerchantForm(enrollmentResponse: EnrollmentResponse): void {
    const data = enrollmentResponse.Data;
    const formValues = {
      FirstName: data?.ContactInformation?.FirstName || '',
      LastName: data?.ContactInformation?.LastName || '',
      Email: data?.ContactInformation?.EmailAddress || data?.MerchantEmail || '',
      BusinessName: data?.BusinessInformation?.Name || '',
      BusinessPhone: data?.BusinessInformation?.PhoneNumber || '',
      Address: data?.BusinessInformation?.Address || '',
      Timezone: data?.BusinessInformation?.TimeZone || '',
    };

    this.merchantForm.patchValue(formValues);

    // Store original values for comparison
    this.originalMerchantFormValues = { ...formValues };

    // Subscribe to form changes to validate significant changes
    this.merchantForm.valueChanges.subscribe(() => {
      this.validateMerchantFormChanges();
    });
  }

  protected populateCashBackForm(enrollmentResponse: EnrollmentResponse): void {
    const data = enrollmentResponse.Data;
    if (data?.CashBackProgram) {
      this.isCustomCashBackSelected = true;
    }

    this.cashBackForm.patchValue({
      Name: data?.CashBackProgram?.Name || `${this.merchantForm.value.BusinessName} Rewards`,
      cashBackPercentage: data?.CashBackProgram?.Percentage || 0,
    });
  }

  protected populatePointsForm(enrollmentResponse: EnrollmentResponse): void {
    const data = enrollmentResponse.Data;
    if (data?.PointsProgram) {
      this.isCustomPointsSelected = true;
    }

    this.pointsForm.patchValue({
      Name: data?.PointsProgram?.Name || `${this.merchantForm.value.BusinessName} Rewards`,
      QualifierAmount: data?.PointsProgram?.QualifierAmount || 1,
      EarnPoints: data?.PointsProgram?.PointsEarned || 1,
      RewardGoal: data?.PointsProgram?.RewardQualifier || 100,
      RewardAmount: data?.PointsProgram?.RewardAmount || 10,
    });
  }

  protected populateInstantOfferForm(enrollmentResponse: EnrollmentResponse): void {
    const data = enrollmentResponse.Data;
    this.isInstantOfferSelected = !!data?.InstantOffer;

    this.instantOfferForm.patchValue({
      InstantReward: data?.InstantOffer?.RewardPercentage || 0,
      MaxRewardAmount: data?.InstantOffer?.MaxRewardAmount || 100.0,
    });
  }

  protected populateBonusOfferForm(enrollmentResponse: EnrollmentResponse): void {
    const data = enrollmentResponse.Data;
    this.isBonusOfferSelected = !!data?.BonusOffer;

    this.bonusOfferForm.patchValue({
      BonusReward: data?.BonusOffer?.RewardPercentage || 0,
      MaxRewardAmount: data?.BonusOffer?.MaxRewardAmount || 0,
    });
  }

  protected populateBonusPointsForm(enrollmentResponse: EnrollmentResponse): void {
    const data = enrollmentResponse.Data;
    this.isBonusPointsSelected = !!data?.EnrollmentPoints;

    this.bonusPointsForm.patchValue({
      BonusPoints: data?.EnrollmentPoints?.Points || 0,
    });
  }

  // Navigation Steps
  // --------------------------------------------------
  protected goToStep(stepIndex: number): void {
    if (stepIndex === 0) {
      this.enrollmentResponse = null;
      this.merchantForm.reset();
      this.originalMerchantFormValues = null;
      this.hasSignificantChanges = false;
      this.sessionId = this.generateSessionId();
      sessionStorage.setItem('sessionId', this.sessionId);
      this.createEnrollmentAnalytics();
    }
    this.currentStepIndex = stepIndex;
  }

  protected prevStep(): void {
    if (this.currentStepIndex === 5) {
      this.goToStep(this.isDefaultLoyaltyProgramSelected ? 1 : 3);
      return;
    }

    if (this.currentStepIndex > 0) {
      this.goToStep(this.currentStepIndex - 1);
    }
  }

  protected saveProcess(): void {
    if (this.isDefaultLoyaltyProgramSelected) {
      // If Sdk_Pline registration type, show Terms and Conditions first
      if (
        this.merchantRegistrationType === MerchantRegistrationType.Sdk_Pline &&
        !this.isAdmin &&
        this.enrollmentStatus === EnrollmentStatusTypes.SelfService
      ) {
        this.goToStep(5);
        this.updateEnrollmentData();
        return;
      }
      this.updateExistingEnrollmentToDefault();
      return;
    }

    // Update the EnrollmentData
    this.updateEnrollmentData();

    this.goToStep(2);
  }

  protected openHelpModal(): void {
    this.helpForm.patchValue({
      FirstName: this.merchantForm.value.FirstName,
    });
    this.helpForm.patchValue({
      BusinessPhone: this.merchantForm.value.BusinessPhone,
    });
    this.showHelpModal = true;
  }

  protected closeHelpModal(): void {
    this.showHelpModal = false;
  }

  // Utils
  // --------------------------------------------------
  protected emailValidator(control: AbstractControl): { [key: string]: any } | null {
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (control.value && !emailPattern.test(control.value)) {
      return { invalidEmail: true };
    }
    return null;
  }

  protected toggleMerchantFormEditing(): void {
    this.isMerchantFormEditing = !this.isMerchantFormEditing;
  }

  protected isCustomLoyaltyProgramSelected: boolean = false;
  protected isDefaultLoyaltyProgramSelected: boolean = true;
  protected selectLoyaltyOption(option: number): void {
    if (option === 1) {
      this.isCustomLoyaltyProgramSelected = false;
      this.isDefaultLoyaltyProgramSelected = true;
    } else if (option === 2) {
      this.isDefaultLoyaltyProgramSelected = false;
      this.isCustomLoyaltyProgramSelected = true;
    }
  }

  protected isCustomCashBackSelected: boolean = false;
  protected isCustomPointsSelected: boolean = false;
  protected selectCustomLoyaltyProgram(option: number): void {
    if (option === 1) {
      this.isCustomCashBackSelected = true;
      this.isCustomPointsSelected = false;
    } else if (option === 2) {
      this.isCustomPointsSelected = true;
      this.isCustomCashBackSelected = false;
    }
  }

  protected isInstantOfferSelected: boolean = false;
  protected isBonusOfferSelected: boolean = false;
  protected isBonusPointsSelected: boolean = false;

  protected selectEnrollmentOffer(option: number): void {
    if (option === 1) {
      this.isInstantOfferSelected = true;
      this.isBonusOfferSelected = false;
      this.isBonusPointsSelected = false;
    } else if (option === 2) {
      this.isBonusOfferSelected = true;
      this.isInstantOfferSelected = false;
      this.isBonusPointsSelected = false;
    } else if (option === 3) {
      this.isBonusPointsSelected = true;
      this.isInstantOfferSelected = false;
      this.isBonusOfferSelected = false;
    }
  }

  protected getImageSrc(): string {
    return this.portalTheme?.PrimaryLogo || '../../assets/enroll-and-pay-logo.png';
  }

  protected validateEnrollmentResponse(enrollmentResponse: EnrollmentResponse): boolean {
    const data = enrollmentResponse.Data;

    if (
      data?.ContactInformation?.FirstName === '' ||
      data?.ContactInformation?.LastName === '' ||
      data?.ContactInformation?.EmailAddress === '' ||
      data?.BusinessInformation?.Name === '' ||
      data?.BusinessInformation?.PhoneNumber === '' ||
      data?.BusinessInformation?.Address === '' ||
      data?.BusinessInformation?.TimeZone === undefined
    ) {
      return false;
    }

    return true;
  }

  protected populateProgramName(): void {
    if (
      this.enrollmentResponse?.Data?.CashBackProgram?.Name === undefined ||
      this.enrollmentResponse?.Data?.CashBackProgram?.Name === ''
    ) {
      this.cashBackForm.patchValue({
        Name: `${this.merchantForm.value.BusinessName} Rewards`,
      });
    }

    if (
      this.enrollmentResponse?.Data?.PointsProgram?.Name === undefined ||
      this.enrollmentResponse?.Data?.PointsProgram?.Name === ''
    ) {
      this.pointsForm.patchValue({
        Name: `${this.merchantForm.value.BusinessName} Rewards`,
      });
    }
  }

  protected onTpnInputFocus(): void {
    if (this.isMobileDevice()) {
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }, 100);
    }
  }

  protected openTpnTutorialModal(): void {
    this.showTpnTutorialModal = true;
  }

  protected closeTpnTutorialModal(): void {
    this.showTpnTutorialModal = false;
  }

  protected onAccordionChange(activeIndexes: number | number[]): void {
    this.isExpanded = true;
    this.createNeedHelpAnalytics();
  }

  protected preventCollapse(event: Event) {
    event.stopPropagation();
  }

  /**
   * Validates if the merchant form has significant changes (more than 2 fields changed)
   * compared to the original populated values
   */
  protected validateMerchantFormChanges(): void {
    if (!this.originalMerchantFormValues) {
      this.hasSignificantChanges = false;
      return;
    }

    const currentValues = this.merchantForm.value;
    const changedFields: string[] = [];

    // Compare each field with original values
    Object.keys(this.originalMerchantFormValues).forEach((key) => {
      const originalValue = this.originalMerchantFormValues[key]?.toString().trim() || '';
      const currentValue = currentValues[key]?.toString().trim() || '';

      // Only consider as significant if the original value was NOT empty
      if (originalValue !== '' && originalValue !== currentValue) {
        changedFields.push(key);
      }
    });

    // Set flag if more than 2 fields with non-empty original values have been changed
    this.hasSignificantChanges = changedFields.length >= 2;
  }

  protected shouldCreateDevice(): boolean {
    if (
      this.enrollmentStatus === EnrollmentStatusTypes.SelfService &&
      this.merchantRegistrationType !== MerchantRegistrationType.Coalition_Flow
    ) {
      return true;
    }
    return false;
  }

  protected shouldSendNotificationEmail(): boolean {
    if (
      this.enrollmentStatus !== EnrollmentStatusTypes.SelfService &&
      this.merchantRegistrationType !== MerchantRegistrationType.Coalition_Flow
    ) {
      return true;
    }
    return false;
  }

  protected acceptTermsAndConditions(): void {
    // Called after user accepts Terms and Conditions
    // Proceed with the appropriate enrollment action based on state
    this.acceptedTC = true;

    if (this.saveEnrollmentOrigin) {
      this.saveEnrollmentOrigin = false;
      this.saveCustomEnrollment();
      return;
    }

    this.updateExistingEnrollmentToDefault();
  }

  protected getButtonLabel(): string {
    if (this.isDefaultLoyaltyProgramSelected && this.enrollmentResponse?.StatusType === 2) {
      return 'Update';
    }

    if (this.isDefaultLoyaltyProgramSelected) {
      return 'Finish';
    }

    return 'Next';
  }

  protected showTerminalReadyMessage(): boolean {
    if (
      (this.enrollmentStatus === EnrollmentStatusTypes.SelfService &&
        this.merchantRegistrationType === this.MerchantRegistrationTypeEnum.Sdk_Pline &&
        !this.hasSignificantChanges &&
        !this.helpNeeded) ||
      (this.enrollmentStatus === EnrollmentStatusTypes.Complete &&
        this.merchantRegistrationType !== MerchantRegistrationType.Coalition_Flow)
    ) {
      return true;
    }
    return false;
  }

  protected showPendingReviewMessage(): boolean {
    if (this.enrollmentStatus === EnrollmentStatusTypes.Pending || this.hasSignificantChanges || this.helpNeeded) {
      return true;
    }
    return false;
  }

  protected showEnrollmentCompleteMessage(): boolean {
    if (this.merchantRegistrationType === this.MerchantRegistrationTypeEnum.Coalition_Flow && !this.helpNeeded) {
      return true;
    }

    return false;
  }

  private generateSessionId(): string {
    let paddedCoalitionId = this.coalitionId;
    if (/^\d{2}$/.test(this.coalitionId)) {
      paddedCoalitionId = '0' + this.coalitionId;
    }
    return `${paddedCoalitionId}-` + Date.now() + '-' + Math.floor(Math.random() * 1000);
  }

  private getMerchantRegistrationAnalyticsType(): number {
    if (this.merchantRegistrationType === MerchantRegistrationType.Coalition_Flow) {
      return TpnAnalyticsTypes.TpnMerchantInfoCoalition;
    }

    if (this.merchantRegistrationType === MerchantRegistrationType.Sdk_Pline) {
      return TpnAnalyticsTypes.TpnLandingPage;
    }

    if (this.merchantRegistrationType === MerchantRegistrationType.Terminal_Qd) {
      return TpnAnalyticsTypes.TpnMerchantInfoQd;
    }
    return TpnAnalyticsTypes.TpnLandingPage;
  }

  private getUpdateEnrollmentAnalyticsType(): number {
    if (this.currentStepIndex === 2) {
      return TpnAnalyticsTypes.TpnCreateEnrollment;
    }

    if (this.currentStepIndex === 3) {
      return TpnAnalyticsTypes.TpnCreateLoyalty;
    }

    if (this.currentStepIndex === 5) {
      return TpnAnalyticsTypes.TpnTermsAndConditions;
    }

    return TpnAnalyticsTypes.TpnCreateEnrollment;
  }
}
